<template>
  <div class="system-message-container">
    <el-card shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-chat-dot-round"></i>
          系统消息管理
        </span>
        <el-button type="primary" icon="el-icon-plus" @click="showSendDialog">
          发送消息
        </el-button>
      </div>

      <!-- 搜索区域 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="消息类型">
          <el-select v-model="searchForm.type" placeholder="全部类型" clearable style="width: 150px">
            <el-option label="系统通知" value="system"></el-option>
            <el-option label="审核通知" value="approval"></el-option>
            <el-option label="警告通知" value="warning"></el-option>
            <el-option label="信息通知" value="info"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="关键词">
          <el-input v-model="searchForm.keyword" placeholder="标题/内容" clearable style="width: 200px"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 消息列表 -->
      <el-table :data="messageList" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="type" label="类型" width="120">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.type === 'system'" type="primary">系统通知</el-tag>
            <el-tag v-else-if="scope.row.type === 'approval'" type="warning">审核通知</el-tag>
            <el-tag v-else-if="scope.row.type === 'warning'" type="danger">警告通知</el-tag>
            <el-tag v-else-if="scope.row.type === 'info'" type="info">信息通知</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="200"></el-table-column>
        <el-table-column prop="content" label="内容" min-width="250" show-overflow-tooltip></el-table-column>
        <el-table-column prop="recipientCount" label="接收人数" width="100">
          <template slot-scope="scope">
            <el-tag type="success">{{ scope.row.recipientCount || 0 }}人</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="发送时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-view" @click="viewDetail(scope.row)">查看</el-button>
            <el-button type="text" icon="el-icon-delete" @click="handleDelete(scope.row.id)" style="color: #f56c6c">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        style="margin-top: 20px; text-align: right">
      </el-pagination>
    </el-card>

    <!-- 发送消息对话框 -->
    <el-dialog
      title="发送系统消息"
      :visible.sync="sendDialogVisible"
      width="700px"
      :close-on-click-modal="false">
      <el-form :model="messageForm" :rules="messageRules" ref="messageForm" label-width="100px">
        <el-form-item label="接收对象" prop="recipientType" required>
          <el-radio-group v-model="messageForm.recipientType" @change="handleRecipientTypeChange">
            <el-radio label="all">全部用户</el-radio>
            <el-radio label="role">指定角色</el-radio>
            <el-radio label="user">指定用户</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="选择角色" v-if="messageForm.recipientType === 'role'" prop="roleIds">
          <el-select v-model="messageForm.roleIds" multiple placeholder="请选择角色" style="width: 100%">
            <el-option
              v-for="role in roleList"
              :key="role.id"
              :label="role.roleName"
              :value="role.id">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="选择用户" v-if="messageForm.recipientType === 'user'" prop="userIds">
          <el-select
            v-model="messageForm.userIds"
            multiple
            filterable
            placeholder="请选择用户"
            style="width: 100%">
            <el-option
              v-for="user in userList"
              :key="user.id"
              :label="`${user.nickname || user.username} (${user.username})`"
              :value="user.id">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="消息类型" prop="type">
          <el-select v-model="messageForm.type" placeholder="请选择消息类型" style="width: 100%">
            <el-option label="系统通知" value="system">
              <i class="el-icon-s-tools" style="color: #667eea"></i> 系统通知
            </el-option>
            <el-option label="审核通知" value="approval">
              <i class="el-icon-document-checked" style="color: #f59e0b"></i> 审核通知
            </el-option>
            <el-option label="警告通知" value="warning">
              <i class="el-icon-warning" style="color: #ef4444"></i> 警告通知
            </el-option>
            <el-option label="信息通知" value="info">
              <i class="el-icon-info" style="color: #3b82f6"></i> 信息通知
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="消息标题" prop="title">
          <el-input v-model="messageForm.title" placeholder="请输入消息标题" maxlength="100" show-word-limit></el-input>
        </el-form-item>

        <el-form-item label="消息内容" prop="content">
          <el-input
            v-model="messageForm.content"
            type="textarea"
            :rows="5"
            placeholder="请输入消息内容"
            maxlength="500"
            show-word-limit>
          </el-input>
        </el-form-item>

        <el-form-item label="相关链接">
          <el-input v-model="messageForm.link" placeholder="选填,如:/resource-review"></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="sendDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="sendMessage" :loading="sending">
          <i class="el-icon-s-promotion"></i> 发送消息
        </el-button>
      </div>
    </el-dialog>

    <!-- 消息详情对话框 -->
    <el-dialog
      title="消息详情"
      :visible.sync="detailDialogVisible"
      width="600px">
      <div v-if="currentMessage" class="message-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="消息ID">{{ currentMessage.id }}</el-descriptions-item>
          <el-descriptions-item label="消息类型">
            <el-tag v-if="currentMessage.type === 'system'" type="primary">系统通知</el-tag>
            <el-tag v-else-if="currentMessage.type === 'approval'" type="warning">审核通知</el-tag>
            <el-tag v-else-if="currentMessage.type === 'warning'" type="danger">警告通知</el-tag>
            <el-tag v-else-if="currentMessage.type === 'info'" type="info">信息通知</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="消息标题" :span="2">{{ currentMessage.title }}</el-descriptions-item>
          <el-descriptions-item label="消息内容" :span="2">
            <div style="white-space: pre-wrap">{{ currentMessage.content }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="相关链接" :span="2">
            {{ currentMessage.link || '无' }}
          </el-descriptions-item>
          <el-descriptions-item label="接收人数">{{ currentMessage.recipientCount || 0 }}人</el-descriptions-item>
          <el-descriptions-item label="发送时间">{{ formatTime(currentMessage.createTime) }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'SystemMessage',
  data() {
    return {
      loading: false,
      messageList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        type: '',
        keyword: ''
      },
      sendDialogVisible: false,
      detailDialogVisible: false,
      sending: false,
      messageForm: {
        recipientType: 'all',
        roleIds: [],
        userIds: [],
        type: 'system',
        title: '',
        content: '',
        link: ''
      },
      messageRules: {
        recipientType: [
          { required: true, message: '请选择接收对象', trigger: 'change' }
        ],
        type: [
          { required: true, message: '请选择消息类型', trigger: 'change' }
        ],
        title: [
          { required: true, message: '请输入消息标题', trigger: 'blur' },
          { min: 2, max: 100, message: '标题长度在2-100个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入消息内容', trigger: 'blur' },
          { min: 2, max: 500, message: '内容长度在2-500个字符', trigger: 'blur' }
        ]
      },
      roleList: [],
      userList: [],
      currentMessage: null
    }
  },
  mounted() {
    this.loadData()
    this.loadRoles()
    this.loadUsers()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/system-message/list', {
          params: {
            current: this.currentPage,
            size: this.pageSize,
            type: this.searchForm.type || null,
            keyword: this.searchForm.keyword || null
          }
        })
        if (res.data.code === 200) {
          this.messageList = res.data.data.records
          this.total = res.data.data.total
        }
      } catch (error) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadRoles() {
      try {
        const res = await this.$axios.get('/admin/role/list')
        if (res.data.code === 200) {
          this.roleList = res.data.data || []
        }
      } catch (error) {
        console.error('加载角色列表失败:', error)
      }
    },
    async loadUsers() {
      try {
        const res = await this.$axios.get('/admin/user/list', {
          params: {
            current: 1,
            size: 1000  // 加载所有用户
          }
        })
        if (res.data.code === 200) {
          this.userList = res.data.data.records || []
        }
      } catch (error) {
        console.error('加载用户列表失败:', error)
      }
    },
    handleRecipientTypeChange() {
      this.messageForm.roleIds = []
      this.messageForm.userIds = []
    },
    showSendDialog() {
      this.messageForm = {
        recipientType: 'all',
        roleIds: [],
        userIds: [],
        type: 'system',
        title: '',
        content: '',
        link: ''
      }
      this.sendDialogVisible = true
      this.$nextTick(() => {
        this.$refs.messageForm.clearValidate()
      })
    },
    async sendMessage() {
      this.$refs.messageForm.validate(async (valid) => {
        if (!valid) return

        // 验证接收对象
        if (this.messageForm.recipientType === 'role' && this.messageForm.roleIds.length === 0) {
          this.$message.warning('请选择至少一个角色')
          return
        }
        if (this.messageForm.recipientType === 'user' && this.messageForm.userIds.length === 0) {
          this.$message.warning('请选择至少一个用户')
          return
        }

        this.sending = true
        try {
          const res = await this.$axios.post('/admin/system-message/send', this.messageForm)
          if (res.data.code === 200) {
            this.$message.success(`消息发送成功，已发送给${res.data.data.count}个用户`)
            this.sendDialogVisible = false
            this.loadData()
          } else {
            this.$message.error(res.data.message || '发送失败')
          }
        } catch (error) {
          this.$message.error('发送失败')
        } finally {
          this.sending = false
        }
      })
    },
    viewDetail(row) {
      this.currentMessage = row
      this.detailDialogVisible = true
    },
    handleDelete(id) {
      this.$confirm('确认删除这条消息吗?删除后无法恢复', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$axios.delete(`/admin/system-message/${id}`)
          if (res.data.code === 200) {
            this.$message.success('删除成功')
            this.loadData()
          }
        } catch (error) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    resetSearch() {
      this.searchForm = {
        type: '',
        keyword: ''
      }
      this.currentPage = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadData()
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.system-message-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.card-title i {
  margin-right: 8px;
  color: #409EFF;
}

.search-form {
  margin-bottom: 20px;
}

.message-detail {
  padding: 10px 0;
}
</style>
